import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Token refresh interval (refresh every 20 minutes)
const TOKEN_REFRESH_INTERVAL = 20 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(true);

  // Refresh token function
  const refreshToken = useCallback(async () => {
    const storedToken = localStorage.getItem('admin_token');
    if (!storedToken) return false;

    try {
      const response = await axios.post(
        `${API}/admin/refresh-token`,
        {},
        { headers: { Authorization: `Bearer ${storedToken}` } }
      );
      
      const { access_token, session_id } = response.data;
      setToken(access_token);
      setSessionId(session_id);
      localStorage.setItem('admin_token', access_token);
      localStorage.setItem('session_id', session_id);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // If refresh fails, log out
      logout();
      return false;
    }
  }, []);

  useEffect(() => {
    // Check for stored token
    const storedToken = localStorage.getItem('admin_token');
    const storedAdmin = localStorage.getItem('admin_email');
    const storedSessionId = localStorage.getItem('session_id');
    
    if (storedToken && storedAdmin) {
      setToken(storedToken);
      setAdmin({ email: storedAdmin });
      setSessionId(storedSessionId);
    }
    
    setLoading(false);
  }, []);

  // Set up automatic token refresh
  useEffect(() => {
    if (!token) return;

    const intervalId = setInterval(() => {
      refreshToken();
    }, TOKEN_REFRESH_INTERVAL);

    return () => clearInterval(intervalId);
  }, [token, refreshToken]);

  const login = (accessToken, adminEmail, newSessionId) => {
    setToken(accessToken);
    setAdmin({ email: adminEmail });
    setSessionId(newSessionId);
    localStorage.setItem('admin_token', accessToken);
    localStorage.setItem('admin_email', adminEmail);
    if (newSessionId) {
      localStorage.setItem('session_id', newSessionId);
    }
  };

  const logout = async () => {
    // Call logout API to invalidate session on server
    const storedToken = localStorage.getItem('admin_token');
    if (storedToken) {
      try {
        await axios.post(
          `${API}/admin/logout`,
          {},
          { headers: { Authorization: `Bearer ${storedToken}` } }
        );
      } catch (error) {
        // Ignore logout API errors
        console.error('Logout API error:', error);
      }
    }

    setToken(null);
    setAdmin(null);
    setSessionId(null);
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_email');
    localStorage.removeItem('session_id');
  };

  return (
    <AuthContext.Provider value={{ 
      admin, 
      token, 
      sessionId,
      login, 
      logout, 
      loading, 
      isAuthenticated: !!token,
      refreshToken
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};